import { Component, OnDestroy, OnInit, computed, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subject, Subscription, debounceTime, distinctUntilChanged } from 'rxjs';
import { MenuItem } from 'primeng/api';
import { Button } from 'primeng/button';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { InputText } from 'primeng/inputtext';
import { Menu } from 'primeng/menu';
import { SelectButton } from 'primeng/selectbutton';
import { Skeleton } from 'primeng/skeleton';

import { BUSQUEDA_NOMBRE_DELAY_MS } from '../../../helpers/busqueda-nombre-delay';
import { chatNoLeido, chatTipoEntrante } from '../../../helpers/chat-no-leido';
import { ordenarConversaciones } from '../../../helpers/chat-orden';
import { chatTienePendiente } from '../../../helpers/chat-tiene-pendiente';
import { ChatConversacion, ChatPerspectiva } from '../../../interfaces/chat.interface';
import { ChatListItemComponent } from '../chat-list-item/chat-list-item.component';

export type ChatListFiltro = 'todos' | 'noLeidos' | 'pendientes';

const FILTROS: { label: string; value: ChatListFiltro }[] = [
  { label: 'Todos', value: 'todos' },
  { label: 'No le\u00eddos', value: 'noLeidos' },
  { label: 'Pendientes', value: 'pendientes' },
];

@Component({
  selector: 'app-chat-list',
  standalone: true,
  imports: [
    FormsModule,
    Button,
    IconField,
    InputIcon,
    InputText,
    Menu,
    SelectButton,
    Skeleton,
    ChatListItemComponent,
  ],
  templateUrl: './chat-list.component.html',
  styleUrl: './chat-list.component.scss',
})
export class ChatListComponent implements OnInit, OnDestroy {
  readonly conversaciones = input<ChatConversacion[]>([]);
  readonly seleccionadaId = input<string | null>(null);
  readonly loading = input(false);
  readonly perspectiva = input<ChatPerspectiva>('admin');
  /** Habilita el menu con la accion de cerrar todos los chats. */
  readonly mostrarAcciones = input(false);
  readonly emptyMessage = input('No hay solicitudes.');
  /** Muestra el nivel al lado del nombre. */
  readonly mostrarNivel = input(false);
  /** Ícono de pregunta si falta nacimiento o reunión. */
  readonly mostrarAvisoDatos = input(false);
  readonly cantCulto = input(1);

  readonly seleccionar = output<string>();
  readonly cerrarTodos = output<void>();

  private readonly busqueda$ = new Subject<string>();
  private busquedaSub?: Subscription;

  readonly filtros = FILTROS;
  readonly busqueda = signal('');
  private readonly busquedaAplicada = signal('');
  readonly esperandoBusqueda = computed(
    () => this.busqueda().trim() !== this.busquedaAplicada().trim(),
  );
  readonly filtro = signal<ChatListFiltro>('todos');

  readonly menuItems: MenuItem[] = [
    {
      label: 'Cerrar chats',
      icon: 'pi pi-check-circle',
      command: () => this.cerrarTodos.emit(),
    },
  ];

  readonly skeletons = [1, 2, 3, 4, 5];

  ngOnInit(): void {
    this.busquedaSub = this.busqueda$
      .pipe(debounceTime(BUSQUEDA_NOMBRE_DELAY_MS), distinctUntilChanged())
      .subscribe((value) => this.busquedaAplicada.set(value));
  }

  ngOnDestroy(): void {
    this.busquedaSub?.unsubscribe();
  }

  onBusqueda(value: string): void {
    this.busqueda.set(value);
    this.busqueda$.next(value);
  }

  readonly visibles = computed(() => {
    const busqueda = this.busquedaAplicada().trim().toLowerCase();
    const filtro = this.filtro();
    const tipoEntrante = chatTipoEntrante(this.perspectiva());

    return ordenarConversaciones(this.conversaciones()).filter((conversacion) => {
      const nombre = `${conversacion.nombre} ${conversacion.apellido}`.toLowerCase();
      if (busqueda && !nombre.includes(busqueda)) {
        return false;
      }
      if (filtro === 'noLeidos') {
        return chatNoLeido(conversacion.mensajes, tipoEntrante);
      }
      if (filtro === 'pendientes') {
        return chatTienePendiente(conversacion);
      }
      return true;
    });
  });

  readonly vacio = computed(() =>
    this.conversaciones().length === 0
      ? this.emptyMessage()
      : 'Ning\u00fan chat coincide con la b\u00fasqueda.',
  );
}
